import { useCallback, useState } from 'react';
import { UseQueryExecute } from 'urql';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { PlusOutline } from '@lyearn/icons';
import { Loader, Stack, Typography, useSnackbar } from '@lyearn/ui';
import useJobByCreatorSubscriptionFn from '@/modules/corporate/modules/bulk-import/hooks/useJobByCreatorSubscription';
import { QuizLanguageInfoFragment } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/fragments/generated/QuizLanguageInfo';
import { CourseContentType, JobPurposeEnum, LanguageEnum } from '@/types/schema';

import { CourseLanguageInfoFragment } from '../../../../graphql/fragments/generated/CourseLanguageInfo';
import { useCreateLocalizedContentsMutation } from '../../../../graphql/mutation/generated/createLocalizedContents';
import { getLanguageName } from '.';

type Props = {
  language: LanguageEnum;
  content: CourseLanguageInfoFragment | QuizLanguageInfoFragment;
  refetchQuery?: UseQueryExecute;
  jobScheduleId: string | null;
};
export const AddButton = ({
  language,
  content,
  refetchQuery,
  jobScheduleId: initialJobScheduleId,
}: Props) => {
  const { t } = useTranslation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [{ fetching: isCreatingLocalizedContent }, createLocalizedContent] =
    useCreateLocalizedContentsMutation();

  const [isAdded, { on }] = useToggle();
  const [jobScheduleId, setJobScheduleId] = useState<string | null>(initialJobScheduleId);

  const onSuccess = useEvent(() => {
    showSnackbar({
      message: t('Course:CreateLocalizedContentSuccess', {
        language: getLanguageName(language),
      }),
    });
    on();
    refetchQuery?.();
  });

  const { fetching: isFetchingJobByCreator } = useJobByCreatorSubscriptionFn({
    pause: !jobScheduleId && !initialJobScheduleId,
    purpose: [JobPurposeEnum.AiBlockLocaleTranslation],
    onSuccess,
  });

  const onAdd = useCallback(async () => {
    const { isLocalizedContent, sourceContent, id } = content;

    await createLocalizedContent({
      input: {
        entityId: isLocalizedContent ? (sourceContent as CourseContentType)?.id || '' : id,
        languageTypes: [language],
      },
    }).then((res) => {
      if (res.data?.createLocalizedContents.languageConfigs[0]?.contentId?.length) {
        const jobScheduleId = res.data?.createLocalizedContents.languageConfigs[0]?.jobScheduleId;
        setJobScheduleId(jobScheduleId || null);
      } else {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      }
    });
  }, [content, createLocalizedContent, language, t, showErrorSnackbar]);

  if (isAdded) {
    return null;
  }

  return (
    <Stack
      alignItems="center"
      className="cursor-pointer px-12 hover:bg-interactive-secondary-hover rounded-l-[9999px] rounded-r-[9999px] py-4 bg-bg-secondary"
      onClick={(event) => {
        if (isCreatingLocalizedContent) {
          return;
        }
        stopEventPropagation(event);
        onAdd();
      }}>
      {isCreatingLocalizedContent || isFetchingJobByCreator ? (
        <Loader className="mx-[9.45px]" size={18} />
      ) : (
        <>
          <PlusOutline className="h-12 w-12" />
          <Typography color="text-text-secondary" variant="label-03">
            {t('Common:Actions.Add')}
          </Typography>
        </>
      )}
    </Stack>
  );
};
